import profileTemplate from "../templates/pages/profile.html?raw";
import NavBar from "../sections/navbar";
import Footer from "../sections/footer";
import { Page } from "./base";
import { getElementById } from "../services/dom";
import { getApi, postApi, deleteApi } from "../services/api";

export default class ProfilePage extends Page {
  private currentUser: any = null;
  private household: any = null;
  private householdMembers: any[] = [];
  private isAdmin: boolean = false;

  constructor() {
    super(profileTemplate);
    this.loadPage();
  }

  private async loadPage(): Promise<void> {
    // Create navbar and footer immediately
    new NavBar();
    new Footer();

    // Setup event listeners
    this.setupListeners();

    // Load data
    await this.loadData();
  }

  private setupListeners(): void {
    // Show/hide add member form
    getElementById("showAddMemberButton").addEventListener("click", () => {
      getElementById("addMemberSection").classList.remove("hidden");
      getElementById("showAddMemberButton").classList.add("hidden");
    });

    getElementById("cancelAddMemberButton").addEventListener("click", () => {
      getElementById("addMemberSection").classList.add("hidden");
      getElementById("showAddMemberButton").classList.remove("hidden");
      this.clearAddMemberForm();
    });

    // Add member form submission
    getElementById("addMemberForm").addEventListener("submit", (e) => {
      e.preventDefault();
      this.handleAddMember();
    });
  }

  private async loadData(): Promise<void> {
    try {
      // Get current user info
      this.currentUser = await getApi(this.apiUrl, "user", this.apiToken);
      console.log("Current user data:", this.currentUser);
      this.displayUserInfo();

      if (this.currentUser.household) {
        this.household = this.currentUser.household;
        this.isAdmin = this.currentUser.is_household_admin || false;
        console.log("Is admin:", this.isAdmin);
        console.log("Current user ID:", this.currentUser.id);

        // Display household info
        getElementById("householdName").textContent = this.household.name || "Unknown";
        getElementById("inviteCode").textContent = this.household.invite_code || "N/A";

        // Show admin-only controls
        if (this.isAdmin) {
          getElementById("showAddMemberButton").classList.remove("hidden");
        }

        // Load household members
        await this.loadHouseholdMembers();
      } else {
        getElementById("householdSection").innerHTML = `
          <div class="text-center text-gray-400 py-8">
            <p>You are not part of a household yet.</p>
            <a href="/household-setup" class="text-blue-400 hover:underline">Join or create a household</a>
          </div>
        `;
      }
    } catch (error) {
      console.error("Error loading profile data:", error);
      this.message.timedMessage("⚠ Error loading profile data ⚠");
    }
  }

  private displayUserInfo(): void {
    getElementById("userFirstName").textContent = this.currentUser.first_name || "--";
    getElementById("userLastName").textContent = this.currentUser.last_name || "--";
    getElementById("userEmail").textContent = this.currentUser.email || "--";
  }

  private async loadHouseholdMembers(): Promise<void> {
    try {
      this.householdMembers = await getApi(this.apiUrl, "household", this.apiToken);
      console.log("Household members data:", this.householdMembers);
      this.displayHouseholdMembers();
    } catch (error) {
      console.error("Error loading household members:", error);
      getElementById("membersList").innerHTML = `
        <div class="text-center text-red-400 py-4">Error loading household members</div>
      `;
    }
  }

  private displayHouseholdMembers(): void {
    const membersList = getElementById("membersList");
    membersList.innerHTML = "";

    if (this.householdMembers.length === 0) {
      membersList.innerHTML = `
        <div class="text-center text-gray-400 py-4">No members found</div>
      `;
      return;
    }

    this.householdMembers.forEach((member: any) => {
      console.log("Processing member:", member);
      console.log("  - is_household_admin:", member.is_household_admin);
      console.log("  - member.id:", member.id);
      console.log("  - this.isAdmin:", this.isAdmin);
      console.log("  - this.currentUser.id:", this.currentUser.id);

      const memberDiv = document.createElement("div");
      memberDiv.className = "px-4 py-3 flex gap-4 text-left";

      // Name
      const nameSpan = document.createElement("span");
      nameSpan.className = "text-white font-semibold min-w-[150px]";
      nameSpan.textContent = `${member.first_name} ${member.last_name}`;
      memberDiv.appendChild(nameSpan);

      // Email
      const emailSpan = document.createElement("span");
      emailSpan.className = "text-gray-400 text-sm flex-1";
      emailSpan.textContent = member.email || "No email";
      memberDiv.appendChild(emailSpan);

      // Admin badge
      if (member.is_household_admin) {
        console.log("  -> Adding admin badge for", member.first_name);
        const adminBadge = document.createElement("span");
        adminBadge.className = "bg-blue-600 text-white text-xs px-2 py-1 rounded";
        adminBadge.textContent = "Admin";
        memberDiv.appendChild(adminBadge);
      }

      // Remove button (X) for admins (but not for themselves)
      if (this.isAdmin && member.id !== this.currentUser.id) {
        console.log("  -> Adding remove button for", member.first_name);
        const removeButton = document.createElement("button");
        removeButton.className = "text-red-500 hover:text-red-400 text-xl font-bold ml-2";
        removeButton.textContent = "×";
        removeButton.title = "Remove member";
        removeButton.addEventListener("click", () => this.handleRemoveMember(member));
        memberDiv.appendChild(removeButton);
      }

      membersList.appendChild(memberDiv);
    });
  }

  private async handleAddMember(): Promise<void> {
    const firstName = (getElementById("memberFirstName") as HTMLInputElement).value;
    const lastName = (getElementById("memberLastName") as HTMLInputElement).value;
    const email = (getElementById("memberEmail") as HTMLInputElement).value;

    const errorDiv = getElementById("addMemberError");
    errorDiv.innerHTML = "";

    if (!firstName || !lastName) {
      errorDiv.innerHTML = `
        <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
          First name and last name are required
        </div>
      `;
      return;
    }

    const addButton = getElementById("addMemberButton") as HTMLButtonElement;
    addButton.disabled = true;
    addButton.textContent = "Adding...";

    try {
      const requestBody: any = {
        first_name: firstName,
        last_name: lastName,
      };

      if (email) {
        requestBody.email = email;
      }

      await postApi(this.apiUrl, "household/members", requestBody, this.apiToken);

      // Success - reload members list
      await this.loadHouseholdMembers();
      this.clearAddMemberForm();
      getElementById("addMemberSection").classList.add("hidden");
      getElementById("showAddMemberButton").classList.remove("hidden");
      this.message.timedMessage("✓ Member added successfully");
    } catch (error: any) {
      console.error("Error adding member:", error);
      errorDiv.innerHTML = `
        <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
          ${error.statusText || "Failed to add member"}
        </div>
      `;
    } finally {
      addButton.disabled = false;
      addButton.textContent = "Add Member";
    }
  }

  private async handleRemoveMember(member: any): Promise<void> {
    const confirmRemove = confirm(
      `Are you sure you want to remove ${member.first_name} ${member.last_name} from the household?`
    );

    if (!confirmRemove) {
      return;
    }

    try {
      await deleteApi(
        this.apiUrl,
        "household/members",
        { member_id: member.id },
        this.apiToken
      );

      // Success - reload members list
      await this.loadHouseholdMembers();
      this.message.timedMessage("✓ Member removed successfully");
    } catch (error: any) {
      console.error("Error removing member:", error);
      this.message.timedMessage("⚠ Failed to remove member ⚠");
    }
  }

  private clearAddMemberForm(): void {
    (getElementById("memberFirstName") as HTMLInputElement).value = "";
    (getElementById("memberLastName") as HTMLInputElement).value = "";
    (getElementById("memberEmail") as HTMLInputElement).value = "";
    getElementById("addMemberError").innerHTML = "";
  }
}
